import React, { useState } from 'react';
import { Box, Typography, Paper, Alert, Button, CircularProgress, Stack, Divider } from '@mui/material';
import axios from 'axios';

export default function Step6Review({ data, summary, onComplete }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [executionResult, setExecutionResult] = useState(null);

  const executeImport = async () => {
    if (!data) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const tenant = localStorage.getItem('tenant') || '';
      
      const response = await axios.post(`${process.env.REACT_APP_API_URL1}/historical-import/execute`, {
        validatedData: data
      }, {
        withCredentials: true,
        headers: { 
          Authorization: `Bearer ${token}`,
          'X-Tenant': tenant
        }
      });

      if (response.data.success) {
        setExecutionResult(response.data.data);
      } else {
        setError(response.data.message || 'Execution failed.');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred during execution');
    } finally {
      setLoading(false);
    }
  };

  if (executionResult) {
    return (
      <Box textAlign="center" py={5}>
        <Typography variant="h5" color="success.main" gutterBottom fontWeight="bold">
          Import Completed Successfully!
        </Typography>
        <Typography variant="body1" color="textSecondary" mb={4}>
          The correct records have been imported. Any records with errors were skipped.
        </Typography>
        
        <Paper sx={{ p: 4, maxWidth: 500, mx: 'auto', bgcolor: '#f8fafc', borderRadius: 3 }}>
          <Stack spacing={2} divider={<Divider />}>
            <Box display="flex" justifyContent="space-between">
              <Typography color="textSecondary">Successfully Imported Staff:</Typography>
              <Typography fontWeight="bold" color="success.main">{executionResult.insertedStaffCount}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography color="textSecondary">Successfully Imported Events:</Typography>
              <Typography fontWeight="bold" color="success.main">{executionResult.insertedEventsCount}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography color="textSecondary">Skipped Staff (Errors):</Typography>
              <Typography fontWeight="bold" color="error.main">{executionResult.skippedStaffCount}</Typography>
            </Box>
          </Stack>
        </Paper>

        <Button 
          variant="contained" 
          color="primary" 
          sx={{ mt: 5 }}
          onClick={() => onComplete(executionResult)}
        >
          Finish & Return
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>5. Review & Execute</Typography>
      <Typography variant="body2" color="textSecondary" mb={3}>
        Review the final summary before injecting the historical data into PeopleHub.
      </Typography>

      <Paper sx={{ p: 3, mb: 3, bgcolor: '#f0fdf4', borderColor: '#bbf7d0', borderWidth: 1, borderStyle: 'solid' }}>
        <Typography variant="subtitle1" fontWeight="bold" color="success.main" gutterBottom>
          Data is ready for import
        </Typography>
        
        {summary && (
          <Box mt={2}>
            <Typography>Total Staff Processed: <strong>{summary.total_staff}</strong></Typography>
            <Typography>Total Historical Events: <strong>{summary.total_events}</strong></Typography>
            <Typography>New Users to Create: <strong>{summary.new_staff_count}</strong></Typography>
            <Typography>Existing Users to Update: <strong>{summary.existing_staff_count}</strong></Typography>
          </Box>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Box textAlign="center" mt={5}>
        <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
          Warning: This action will insert historical data into the live database. Make sure you have reviewed the timeline mapping in Step 3.
        </Alert>
        
        <Button 
          variant="contained" 
          color="error" 
          size="large"
          onClick={executeImport}
          disabled={loading || !data}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {loading ? 'Executing Import...' : 'Execute Historical Import'}
        </Button>
      </Box>
    </Box>
  );
}
